type MenuOptionLike = {
  text?: string
  divider?: boolean
  header?: string
  onClick?: (() => void) | undefined
}

type BuildFilterSearchMenuOptionsArgs<T extends MenuOptionLike> = {
  query: string
  menuOptions: T[]
  autocompleteSuggestions: T[]
  jobNumberOption?: T | null
  includeAutocompleteSuggestions?: boolean
}

export const createMenuDivider = <T extends MenuOptionLike>() => ({ divider: true }) as T

export const isSelectableMenuOption = <T extends MenuOptionLike>(option: T | null | undefined) =>
  !!option && !option.divider && !option.header && typeof option.onClick === 'function'

export const findFirstSelectableMenuOption = <T extends MenuOptionLike>(options: T[]) =>
  options.find((option) => isSelectableMenuOption(option)) ?? null

export function buildFilterSearchMenuOptions<T extends MenuOptionLike>({
  query,
  menuOptions,
  autocompleteSuggestions,
  jobNumberOption,
  includeAutocompleteSuggestions = true,
}: BuildFilterSearchMenuOptionsArgs<T>): T[] {
  const trimmedQuery = query.trim().toLowerCase()
  if (!trimmedQuery) {
    return menuOptions
  }

  const matchingFilters = menuOptions.filter(
    (option) =>
      option &&
      !option.divider &&
      !option.header &&
      option.text?.toLowerCase().includes(trimmedQuery),
  )

  const results: T[] = []

  const appendSection = (items: T[]) => {
    if (items.length === 0) {
      return
    }

    if (results.length > 0) {
      results.push(createMenuDivider<T>())
    }

    results.push(...items)
  }

  if (jobNumberOption) {
    appendSection([jobNumberOption])
  }

  appendSection(matchingFilters)

  if (includeAutocompleteSuggestions) {
    appendSection(autocompleteSuggestions)
  }

  return results
}
